import React, { createContext, useContext, useEffect, useCallback } from "react";
import useSWRInfinite from "swr/infinite";
import ResultsList from "./ResultsList";
import styled from "@emotion/styled";
import { DegreePlan, Rule, Fulfillment } from "@/types";
import { PanelContainer, PanelHeader } from "../FourYearPlan/PanelCommon";
import { GrayIcon } from "../common/bulma_derived_components";

interface SearchPanelContextType {
    setSearchPanelOpen: (arg0: boolean) => void;
    searchPanelOpen: boolean;
    setSearchRuleId: (arg0: Rule["id"] | null) => void;
    searchRuleId: Rule["id"] | null;
    setSearchFulfillments: (arg0: Fulfillment[]) => void,
    searchFulfillments: Fulfillment[],
    setSearchRuleQuery: (arg0: string | null) => void;
    searchRuleQuery: string | null; // the q string associated with the rulez
}

export const SearchPanelContext = createContext<SearchPanelContextType>({
    setSearchPanelOpen: (arg0) => {},
    searchPanelOpen: false,
    setSearchRuleId: (arg0) => {},
    searchRuleId: null,
    setSearchFulfillments: (arg0) => {},
    searchFulfillments: [],
    setSearchRuleQuery: (arg0) => {},
    searchRuleQuery: ""
});


const SearchPanelBody = styled.div`
    margin: .6rem;
    overflow-y: auto;
`

const SearchPanelResult = styled.div`
    margin-top: .5rem;
    overflow-x: hidden;
`

const SearchContainer = styled.div`
    position: sticky;
    top: 0;
    padding: .5rem .75rem;
    background-color: var(--background-grey);
    border-radius: .75rem;
    display: flex;
    align-items: center;
    gap: 1rem;
`

const SearchField = styled.input`
    flex: 1;
    border: none;
    outline: none;
    color: black;
    background: none;
    font: inherit;
`;

const PanelTitle = styled.div`
    font-weight: 700;
`

const SearchPanelHeader = styled(PanelHeader)`
    display: flex;
    justify-content: space-between;
    font-size: 1.25rem;
`

interface SearchPanelProp {
    activeDegreeplanId: DegreePlan["id"] | null;
}

export const SearchPanel = ({ activeDegreeplanId }: SearchPanelProp) => {
    const { 
        setSearchPanelOpen, 
        searchRuleId: ruleId, 
        setSearchRuleId,
        searchRuleQuery: ruleQuery,
        searchFulfillments: fulfillments
    } = useContext(SearchPanelContext); 


    // queryString and searchRuleQuery are different (queryString is the actual query e.g., "World Civ",
    // and searchRuleQuery is a q object)
    const [queryString, setQueryString] = React.useState<string>("");

    React.useEffect(() => {
        setQueryString("");
    }, [ruleId])

    const handleCloseSearch = () => {
        setQueryString(""); 
        setSearchPanelOpen(false);
        setSearchRuleId(null);
    }

    return (
        <PanelContainer>
            <SearchPanelHeader>
                <PanelTitle>Search</PanelTitle>
                <label onClick={handleCloseSearch}>
                    <i className="fa fa-times" />
                </label>
            </SearchPanelHeader>
            <SearchPanelBody>
                <SearchContainer
                >
                    <GrayIcon>
                        <i className="fas fa-search fa-lg" />
                    </GrayIcon>
                    <SearchField
                        aria-label="search for a course"
                        autoFocus
                        type="text"
                        value={queryString}
                        onChange={(e) => {setQueryString(e.target.value)}}
                        autoComplete="off"
                        placeholder={!ruleId ? "Search for a course!" : `Filtering for a requirement`}
                    />
                </SearchContainer>
                <SearchResults ruleId={ruleId} query={queryString} activeDegreeplanId={activeDegreeplanId} fulfillments={fulfillments}/> 
            </SearchPanelBody>
        </PanelContainer>
    )
}

export const useDebounce = (value: any, delay: number) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const PAGE_SIZE = 50;

const buildSearchKey = (ruleId: Rule["id"] | null, query: string) =>
    (pageIndex: number, previousPageData: any): string | null => {
        if (!(query.length >= 3 || ruleId)) return null;
        // Stop fetching if we got fewer results than a full page
        if (previousPageData && !previousPageData.next) return null;
        const page = pageIndex + 1; // DRF pages are 1-indexed
        return `api/base/all/search/courses?search=${query}${ruleId ? `&rule_ids=${ruleId}` : ""}&page=${page}&page_size=${PAGE_SIZE}`;
    }

interface SearchResultsProps {
    ruleId: Rule["id"] | null,
    query: string,
    activeDegreeplanId: DegreePlan["id"] | null,
    fulfillments: Fulfillment[]
}
const SearchResults = ({ ruleId, query, activeDegreeplanId, fulfillments }: SearchResultsProps) => {
    const DISABLE_SEARCH = false
    const debouncedQuery = useDebounce(query, 400)
    const { data, size, setSize, isLoading: isLoadingCourses, isValidating } = useSWRInfinite(
        DISABLE_SEARCH ? () => null : buildSearchKey(ruleId, debouncedQuery),
        { revalidateFirstPage: false }
    );

    const courses = data ? data.flatMap((page) => page.results ?? []) : [];
    const isLoadingMore = size > 1 && isValidating && data && typeof data[size - 1] === "undefined";
    const hasMore = data ? !!data[data.length - 1]?.next : false;

    const loadMore = useCallback(() => {
        if (!isValidating && hasMore) {
            setSize((s) => s + 1);
        }
    }, [isValidating, hasMore, setSize]);

    return (
        <>
            <SearchPanelResult>
                <ResultsList
                activeDegreeplanId={activeDegreeplanId}
                ruleId={ruleId}
                courses={courses}
                fulfillments={fulfillments}
                isLoading={isLoadingCourses}
                isLoadingMore={!!isLoadingMore}
                hasMore={hasMore}
                loadMore={loadMore}
                />
            </SearchPanelResult>
        </>
    )
}
